import { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Edit, Trash2, Download, X, Receipt, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { API, statusColors, formatDate, formatCurrency } from "./utils";

export function PaymentsContent({ projectId, expenses, invoices, payments, onRefresh, canWrite, token }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [projectInvoices, setProjectInvoices] = useState([]);
  const [projectExpenses, setProjectExpenses] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Form states
  const [showPOForm, setShowPOForm] = useState(false);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [editingPO, setEditingPO] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Helper to extract error message from API response
  const getErrorMessage = (error, defaultMsg) => {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail[0]?.msg || detail[0]?.message || defaultMsg;
    }
    return defaultMsg;
  };

  // Default form data
  const defaultPOItem = { description: "", unit_price: 0, quantity: 1, hsn: "", rate: 0 };
  const [poForm, setPOForm] = useState({
    vendor_name: "", vendor_address: "", vendor_gst: "", vendor_email: "", vendor_phone: "",
    po_date: new Date().toISOString().split('T')[0], due_date: "",
    items: [{ ...defaultPOItem }], terms: [], notes: "",
    gst_rate: 18, include_gst: true
  });

  const defaultInvoiceItem = { description: "", hsn: "", rate: 0, quantity: 1 };
  const [invoiceForm, setInvoiceForm] = useState({
    client_name: "", client_address: "", client_state: "", client_email: "", client_phone: "",
    client_gst: "", client_pan: "", client_reference: "",
    invoice_date: new Date().toISOString().split('T')[0], due_date: "",
    items: [{ ...defaultInvoiceItem }], gst_rate: 18, notes: "",
    manual_id: ""  // Optional manual ID
  });

  const [expenseForm, setExpenseForm] = useState({
    description: "", category: "Materials", amount: 0, payment_mode: "Bank Transfer",
    transaction_date: new Date().toISOString().split('T')[0], remarks: "", status: "Pending"
  });

  const [paymentForm, setPaymentForm] = useState({
    amount: 0, payment_date: new Date().toISOString().split('T')[0],
    payment_mode: "Bank Transfer", reference_no: "", remarks: ""
  });

  useEffect(() => { fetchData(); }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [posRes, invsRes, analyticsRes, expRes] = await Promise.all([
        axios.get(`${API}/purchase-orders/project/${projectId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/project-invoices/project/${projectId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/project-invoices/analytics/project/${projectId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/projects/${projectId}/expenses`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setPurchaseOrders(posRes.data);
      setProjectInvoices(invsRes.data);
      setAnalytics(analyticsRes.data);
      setProjectExpenses(expRes.data);
    } catch (error) { console.error("Failed to fetch finance data", error); }
    finally { setLoading(false); }
  };

  // PO Handlers
  const resetPOForm = () => {
    setPOForm({ vendor_name: "", vendor_address: "", vendor_gst: "", vendor_email: "", vendor_phone: "",
      po_date: new Date().toISOString().split('T')[0], due_date: "", items: [{ ...defaultPOItem }], terms: [], notes: "",
      gst_rate: 18, include_gst: true });
    setEditingPO(null);
    setShowPOForm(false);
  };

  const handleEditPO = async (po) => {
    const res = await axios.get(`${API}/purchase-orders/${po.id}`, { headers: { Authorization: `Bearer ${token}` } });
    setPOForm({
      vendor_name: res.data.vendor_name, vendor_address: res.data.vendor_address, vendor_gst: res.data.vendor_gst || "",
      vendor_email: res.data.vendor_email || "", vendor_phone: res.data.vendor_phone || "",
      po_date: res.data.po_date, due_date: res.data.due_date,
      items: res.data.items.map(i => ({ description: i.description, unit_price: i.unit_price, quantity: i.quantity, hsn: i.hsn || "", rate: i.rate })),
      terms: res.data.terms || [], notes: res.data.notes || "",
      gst_rate: res.data.gst_rate || 18, include_gst: res.data.include_gst !== false
    });
    setEditingPO(po);
    setShowPOForm(true);
  };

  const handleSubmitPO = async (e) => {
    e.preventDefault();
    if (!poForm.items[0]?.description) { toast.error("Add at least one item"); return; }
    setSubmitting(true);
    try {
      const payload = { project_id: projectId, ...poForm };
      if (editingPO) {
        await axios.put(`${API}/purchase-orders/${editingPO.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Purchase Order updated!");
      } else {
        await axios.post(`${API}/purchase-orders`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Purchase Order created!");
      }
      resetPOForm();
      fetchData();
    } catch (error) { toast.error(getErrorMessage(error, "Failed to save PO")); }
    finally { setSubmitting(false); }
  };

  const handleDeletePO = async (id) => {
    if (!window.confirm("Delete this purchase order?")) return;
    try {
      await axios.delete(`${API}/purchase-orders/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Purchase Order deleted");
      fetchData();
    } catch (error) { toast.error("Failed to delete"); }
  };

  const handleDownloadPOPdf = async (id) => {
    try {
      const res = await axios.get(`${API}/purchase-orders/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PO_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded");
    } catch (error) { toast.error("Failed to download PDF"); }
  };

  // Invoice Handlers
  const resetInvoiceForm = () => {
    setInvoiceForm({ client_name: "", client_address: "", client_state: "", client_email: "", client_phone: "",
      client_gst: "", client_pan: "", client_reference: "",
      invoice_date: new Date().toISOString().split('T')[0], due_date: "",
      items: [{ ...defaultInvoiceItem }], gst_rate: 18, notes: "", manual_id: "" });
    setEditingInvoice(null);
    setShowInvoiceForm(false);
  };

  const handleEditInvoice = async (inv) => {
    const res = await axios.get(`${API}/project-invoices/${inv.id}`, { headers: { Authorization: `Bearer ${token}` } });
    setInvoiceForm({
      client_name: res.data.client_name, client_address: res.data.client_address, client_state: res.data.client_state || "",
      client_email: res.data.client_email || "", client_phone: res.data.client_phone || "",
      client_gst: res.data.client_gst || "", client_pan: res.data.client_pan || "", client_reference: res.data.client_reference || "",
      invoice_date: res.data.invoice_date, due_date: res.data.due_date,
      items: res.data.items.map(i => ({ description: i.description, hsn: i.hsn || "", rate: i.rate, quantity: i.quantity })),
      gst_rate: res.data.gst_rate || 18, notes: res.data.notes || ""
    });
    setEditingInvoice(inv);
    setShowInvoiceForm(true);
  };

  const handleSubmitInvoice = async (e) => {
    e.preventDefault();
    if (!invoiceForm.items[0]?.description) { toast.error("Add at least one item"); return; }
    setSubmitting(true);
    try {
      const payload = { 
        project_id: projectId, 
        ...invoiceForm,
        manual_id: invoiceForm.manual_id || null  // Include manual ID
      };
      if (editingInvoice) {
        await axios.put(`${API}/project-invoices/${editingInvoice.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Invoice updated!");
      } else {
        await axios.post(`${API}/project-invoices`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Invoice created!");
      }
      resetInvoiceForm();
      fetchData();
    } catch (error) { toast.error(getErrorMessage(error, "Failed to save invoice")); }
    finally { setSubmitting(false); }
  };

  const handleDeleteInvoice = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await axios.delete(`${API}/project-invoices/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Invoice deleted");
      fetchData();
    } catch (error) { toast.error("Failed to delete"); }
  };

  const handleDownloadInvoicePdf = async (id) => {
    try {
      const res = await axios.get(`${API}/project-invoices/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded");
    } catch (error) { toast.error("Failed to download PDF"); }
  };

  // Expense Handlers
  const resetExpenseForm = () => {
    setExpenseForm({ description: "", category: "Materials", amount: 0, payment_mode: "Bank Transfer",
      transaction_date: new Date().toISOString().split('T')[0], remarks: "", status: "Pending" });
    setEditingExpense(null);
    setShowExpenseForm(false);
  };

  const handleEditExpense = (expense) => {
    setExpenseForm({
      description: expense.description || "",
      category: expense.category || "Materials",
      amount: expense.amount || 0,
      payment_mode: expense.payment_mode || "Bank Transfer",
      transaction_date: expense.transaction_date || new Date().toISOString().split('T')[0],
      remarks: expense.remarks || "",
      status: expense.status || "Pending"
    });
    setEditingExpense(expense);
    setShowExpenseForm(true);
  };

  const handleUpdateExpenseStatus = async (expense, newStatus) => {
    try {
      await axios.put(`${API}/projects/${projectId}/expenses/${expense.id}`, 
        { ...expense, status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Expense status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to update expense status"));
    }
  };

  const handleSubmitExpense = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { 
        ...expenseForm 
      };
      if (editingExpense) {
        await axios.put(`${API}/projects/${projectId}/expenses/${editingExpense.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Expense updated!");
      } else {
        await axios.post(`${API}/projects/${projectId}/expenses`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Expense added!");
      }
      resetExpenseForm();
      fetchData();
    } catch (error) { toast.error(getErrorMessage(error, "Failed to save expense")); }
    finally { setSubmitting(false); }
  };

  // Payment Handlers
  const openPaymentForm = (type, item) => {
    setPaymentTarget({ type, item });
    setPaymentForm({ amount: item.balance_amount || 0, payment_date: new Date().toISOString().split('T')[0],
      payment_mode: "Bank Transfer", reference_no: "", remarks: "" });
    setShowPaymentForm(true);
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const endpoint = paymentTarget.type === 'po' 
        ? `${API}/purchase-orders/${paymentTarget.item.id}/payments`
        : `${API}/project-invoices/${paymentTarget.item.id}/payments`;
      await axios.post(endpoint, paymentForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Payment recorded!");
      setShowPaymentForm(false);
      setPaymentTarget(null);
      fetchData();
    } catch (error) { toast.error(getErrorMessage(error, "Failed to record payment")); }
    finally { setSubmitting(false); }
  };

  // Item handlers for PO
  const handlePOItemChange = (idx, field, value) => {
    const items = [...poForm.items];
    items[idx] = { ...items[idx], [field]: ['unit_price', 'quantity', 'rate'].includes(field) ? parseFloat(value) || 0 : value };
    setPOForm({ ...poForm, items });
  };
  const addPOItem = () => setPOForm({ ...poForm, items: [...poForm.items, { ...defaultPOItem }] });
  const removePOItem = (idx) => poForm.items.length > 1 && setPOForm({ ...poForm, items: poForm.items.filter((_, i) => i !== idx) });

  // Item handlers for Invoice
  const handleInvoiceItemChange = (idx, field, value) => {
    const items = [...invoiceForm.items];
    items[idx] = { ...items[idx], [field]: ['rate', 'quantity'].includes(field) ? parseFloat(value) || 0 : value };
    setInvoiceForm({ ...invoiceForm, items });
  };
  const addInvoiceItem = () => setInvoiceForm({ ...invoiceForm, items: [...invoiceForm.items, { ...defaultInvoiceItem }] });
  const removeInvoiceItem = (idx) => invoiceForm.items.length > 1 && setInvoiceForm({ ...invoiceForm, items: invoiceForm.items.filter((_, i) => i !== idx) });

  const calculatePOTotal = () => poForm.items.reduce((sum, i) => sum + (i.quantity * i.rate), 0);
  const calculateInvoiceSubtotal = () => invoiceForm.items.reduce((sum, i) => sum + (i.quantity * i.rate), 0);
  const calculateInvoiceGST = () => calculateInvoiceSubtotal() * (invoiceForm.gst_rate / 100);
  const calculateInvoiceTotal = () => calculateInvoiceSubtotal() + calculateInvoiceGST();

  if (loading) return <div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brillien-600" /></div>;

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="purchase-orders" data-testid="tab-po">Purchase Orders</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses</TabsTrigger>
        </TabsList>

        {/* OVERVIEW TAB */}
        <TabsContent value="overview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="p-4 bg-blue-50 border-0">
              <p className="text-sm text-blue-700">Total Invoiced</p>
              <p className="text-xl font-bold text-blue-900">Rs. {(analytics?.invoices?.total_invoiced || 0).toLocaleString()}</p>
            </Card>
            <Card className="p-4 bg-emerald-50 border-0">
              <p className="text-sm text-emerald-700">Received</p>
              <p className="text-xl font-bold text-emerald-900">Rs. {(analytics?.invoices?.total_received || 0).toLocaleString()}</p>
            </Card>
            <Card className="p-4 bg-amber-50 border-0">
              <p className="text-sm text-amber-700">Receivables</p>
              <p className="text-xl font-bold text-amber-900">Rs. {(analytics?.invoices?.total_receivable || 0).toLocaleString()}</p>
            </Card>
            <Card className="p-4 bg-red-50 border-0">
              <p className="text-sm text-red-700">Expenses</p>
              <p className="text-xl font-bold text-red-900">Rs. {(analytics?.expenses?.total || 0).toLocaleString()}</p>
            </Card>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Card className="p-4">
              <h5 className="font-semibold mb-3">PO Summary</h5>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span>Total PO Value:</span><span className="font-medium">Rs. {(analytics?.purchase_orders?.total_value || 0).toLocaleString()}</span></div>
                <div className="flex justify-between"><span>Paid:</span><span className="font-medium text-green-600">Rs. {(analytics?.purchase_orders?.total_paid || 0).toLocaleString()}</span></div>
                <div className="flex justify-between"><span>Pending:</span><span className="font-medium text-amber-600">Rs. {(analytics?.purchase_orders?.total_pending || 0).toLocaleString()}</span></div>
              </div>
            </Card>
            <Card className="p-4">
              <h5 className="font-semibold mb-3">Net Position</h5>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span>Total Income:</span><span className="font-medium text-green-600">Rs. {(analytics?.summary?.total_income || 0).toLocaleString()}</span></div>
                <div className="flex justify-between"><span>Total Outflow:</span><span className="font-medium text-red-600">Rs. {(analytics?.summary?.total_outflow || 0).toLocaleString()}</span></div>
                <div className="flex justify-between border-t pt-2"><span>Net:</span><span className={`font-bold ${(analytics?.summary?.net_position || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>Rs. {(analytics?.summary?.net_position || 0).toLocaleString()}</span></div>
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* PURCHASE ORDERS TAB */}
        <TabsContent value="purchase-orders">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold">Purchase Orders</h4>
            {canWrite && !showPOForm && <Button onClick={() => setShowPOForm(true)} className="bg-brillien-600 hover:bg-brillien-700" data-testid="create-po-btn"><Plus className="h-4 w-4 mr-2" />Create PO</Button>}
          </div>

          {showPOForm && (
            <Card className="p-5 mb-4 border-brillien-200 bg-brillien-50/50">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-semibold">{editingPO ? `Edit PO - ${editingPO.po_number}` : "New Purchase Order"}</h4>
                <Button variant="ghost" size="sm" onClick={resetPOForm}><X className="h-4 w-4" /></Button>
              </div>
              <form onSubmit={handleSubmitPO} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Vendor Name *</Label><Input value={poForm.vendor_name} onChange={e => setPOForm({...poForm, vendor_name: e.target.value})} required /></div>
                  <div><Label>Vendor Address</Label><Input value={poForm.vendor_address} onChange={e => setPOForm({...poForm, vendor_address: e.target.value})} /></div>
                  <div><Label>Vendor GST</Label><Input value={poForm.vendor_gst} onChange={e => setPOForm({...poForm, vendor_gst: e.target.value})} /></div>
                  <div><Label>Vendor Email</Label><Input type="email" value={poForm.vendor_email} onChange={e => setPOForm({...poForm, vendor_email: e.target.value})} /></div>
                  <div><Label>PO Date *</Label><Input type="date" value={poForm.po_date} onChange={e => setPOForm({...poForm, po_date: e.target.value})} required /></div>
                  <div><Label>Due Date</Label><Input type="date" value={poForm.due_date} onChange={e => setPOForm({...poForm, due_date: e.target.value})} /></div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2"><Label>Items</Label><Button type="button" size="sm" variant="outline" onClick={addPOItem}><Plus className="h-3 w-3 mr-1" />Add Item</Button></div>
                  <Table>
                    <TableHeader><TableRow className="bg-slate-100"><TableHead>Description</TableHead><TableHead>HSN</TableHead><TableHead>Qty</TableHead><TableHead>Rate</TableHead><TableHead>Amount</TableHead><TableHead></TableHead></TableRow></TableHeader>
                    <TableBody>
                      {poForm.items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell><Input value={item.description} onChange={e => handlePOItemChange(idx, 'description', e.target.value)} placeholder="Item description" /></TableCell>
                          <TableCell><Input value={item.hsn} onChange={e => handlePOItemChange(idx, 'hsn', e.target.value)} placeholder="HSN" className="w-24" /></TableCell>
                          <TableCell><Input type="number" value={item.quantity} onChange={e => handlePOItemChange(idx, 'quantity', e.target.value)} className="w-20" /></TableCell>
                          <TableCell><Input type="number" value={item.rate} onChange={e => handlePOItemChange(idx, 'rate', e.target.value)} className="w-24" /></TableCell>
                          <TableCell className="font-medium">Rs. {(item.quantity * item.rate).toLocaleString()}</TableCell>
                          <TableCell><Button type="button" variant="ghost" size="sm" onClick={() => removePOItem(idx)} disabled={poForm.items.length === 1}><Trash2 className="h-4 w-4 text-red-500" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="text-right font-bold mt-2">Subtotal: Rs. {calculatePOTotal().toLocaleString()}</div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <input 
                        type="checkbox" 
                        id="include_gst"
                        checked={poForm.include_gst} 
                        onChange={e => setPOForm({...poForm, include_gst: e.target.checked})}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <Label htmlFor="include_gst">Include GST</Label>
                    </div>
                    {poForm.include_gst && (
                      <div className="flex items-center gap-2">
                        <Label>GST Rate:</Label>
                        <Input 
                          type="number" 
                          value={poForm.gst_rate} 
                          onChange={e => setPOForm({...poForm, gst_rate: parseFloat(e.target.value) || 0})} 
                          className="w-20"
                          min="0"
                          max="100"
                        />
                        <span>%</span>
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    {poForm.include_gst && (
                      <div className="text-sm text-slate-600 mb-1">
                        GST ({poForm.gst_rate}%): Rs. {(calculatePOTotal() * (poForm.gst_rate / 100)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </div>
                    )}
                    <div className="font-bold text-lg">
                      Total: Rs. {(calculatePOTotal() * (1 + (poForm.include_gst ? poForm.gst_rate / 100 : 0))).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </div>
                  </div>
                </div>
                <div><Label>Notes</Label><Textarea value={poForm.notes} onChange={e => setPOForm({...poForm, notes: e.target.value})} rows={2} placeholder="Additional notes..." /></div>
                <div>
                  <Label>Terms & Conditions</Label>
                  <Textarea 
                    value={poForm.terms?.join('\n') || ''} 
                    onChange={e => setPOForm({...poForm, terms: e.target.value.split('\n').filter(t => t.trim())})} 
                    rows={4} 
                    placeholder="Enter each term on a new line, e.g.:&#10;Payment due within 30 days&#10;Delivery charges extra&#10;Quality as per sample"
                    data-testid="po-terms-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Enter each term on a new line. These will appear in the PO PDF.</p>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={submitting} className="bg-brillien-600 hover:bg-brillien-700">{submitting ? "Saving..." : editingPO ? "Update PO" : "Create PO"}</Button>
                  <Button type="button" variant="outline" onClick={resetPOForm}>Cancel</Button>
                </div>
              </form>
            </Card>
          )}

          {purchaseOrders.length === 0 && !showPOForm ? (
            <div className="text-center py-12 text-slate-500"><Receipt className="h-12 w-12 mx-auto mb-3 text-slate-300" /><p>No purchase orders created</p></div>
          ) : !showPOForm && (
            <Table>
              <TableHeader>
                <TableRow><TableHead>PO Number</TableHead><TableHead>Vendor</TableHead><TableHead>Date</TableHead><TableHead>Total</TableHead><TableHead>Paid</TableHead><TableHead>Balance</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {purchaseOrders.map(po => (
                  <TableRow key={po.id}>
                    <TableCell className="font-mono text-sm">{po.po_number}</TableCell>
                    <TableCell>{po.vendor_name}</TableCell>
                    <TableCell>{formatDate(po.po_date)}</TableCell>
                    <TableCell className="font-medium">Rs. {(po.total_amount || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-green-600">Rs. {(po.paid_amount || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-amber-600">Rs. {(po.balance_amount || 0).toLocaleString()}</TableCell>
                    <TableCell><Badge className={statusColors[po.status]}>{po.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {canWrite && po.balance_amount > 0 && <Button variant="ghost" size="sm" onClick={() => openPaymentForm('po', po)} className="text-green-600" title="Record Payment"><DollarSign className="h-4 w-4" /></Button>}
                        {canWrite && <Button variant="ghost" size="sm" onClick={() => handleEditPO(po)} className="text-blue-600"><Edit className="h-4 w-4" /></Button>}
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadPOPdf(po.id)} className="text-brillien-600"><Download className="h-4 w-4" /></Button>
                        {canWrite && <Button variant="ghost" size="sm" onClick={() => handleDeletePO(po.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TabsContent>

        {/* INVOICES TAB */}
        <TabsContent value="invoices">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold">Invoices</h4>
            {canWrite && !showInvoiceForm && <Button onClick={() => setShowInvoiceForm(true)} className="bg-brillien-600 hover:bg-brillien-700" data-testid="create-invoice-btn"><Plus className="h-4 w-4 mr-2" />Create Invoice</Button>}
          </div>

          {showInvoiceForm && (
            <Card className="p-5 mb-4 border-brillien-200 bg-brillien-50/50">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-semibold">{editingInvoice ? `Edit Invoice - ${editingInvoice.invoice_number}` : "New Tax Invoice"}</h4>
                <Button variant="ghost" size="sm" onClick={resetInvoiceForm}><X className="h-4 w-4" /></Button>
              </div>
              <form onSubmit={handleSubmitInvoice} className="space-y-4">
                {/* Manual ID field - only show when creating new invoice */}
                {!editingInvoice && (
                  <div className="bg-slate-50 dark:bg-slate-700/50 p-3 rounded-lg border border-dashed border-slate-300 dark:border-slate-400">
                    <Label className="text-sm font-medium">
                      Invoice ID <span className="text-slate-400 font-normal">(Optional - leave blank for auto-generated ID)</span>
                    </Label>
                    <Input 
                      value={invoiceForm.manual_id || ""} 
                      onChange={e => setInvoiceForm({...invoiceForm, manual_id: e.target.value})}
                      placeholder="e.g., INV-CUSTOM-001"
                      className="mt-1"
                      data-testid="invoice-manual-id"
                    />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Client Name *</Label><Input value={invoiceForm.client_name} onChange={e => setInvoiceForm({...invoiceForm, client_name: e.target.value})} required /></div>
                  <div><Label>Client Address</Label><Input value={invoiceForm.client_address} onChange={e => setInvoiceForm({...invoiceForm, client_address: e.target.value})} /></div>
                  <div><Label>Client State</Label><Input value={invoiceForm.client_state} onChange={e => setInvoiceForm({...invoiceForm, client_state: e.target.value})} /></div>
                  <div><Label>Client GST</Label><Input value={invoiceForm.client_gst} onChange={e => setInvoiceForm({...invoiceForm, client_gst: e.target.value})} /></div>
                  <div><Label>Invoice Date *</Label><Input type="date" value={invoiceForm.invoice_date} onChange={e => setInvoiceForm({...invoiceForm, invoice_date: e.target.value})} required /></div>
                  <div><Label>Due Date</Label><Input type="date" value={invoiceForm.due_date} onChange={e => setInvoiceForm({...invoiceForm, due_date: e.target.value})} /></div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2"><Label>Items</Label><Button type="button" size="sm" variant="outline" onClick={addInvoiceItem}><Plus className="h-3 w-3 mr-1" />Add Item</Button></div>
                  <Table>
                    <TableHeader><TableRow className="bg-slate-100"><TableHead>Description</TableHead><TableHead>HSN</TableHead><TableHead>Qty</TableHead><TableHead>Rate</TableHead><TableHead>Amount</TableHead><TableHead></TableHead></TableRow></TableHeader>
                    <TableBody>
                      {invoiceForm.items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell><Input value={item.description} onChange={e => handleInvoiceItemChange(idx, 'description', e.target.value)} placeholder="Service description" /></TableCell>
                          <TableCell><Input value={item.hsn} onChange={e => handleInvoiceItemChange(idx, 'hsn', e.target.value)} placeholder="HSN/SAC" className="w-24" /></TableCell>
                          <TableCell><Input type="number" value={item.quantity} onChange={e => handleInvoiceItemChange(idx, 'quantity', e.target.value)} className="w-20" /></TableCell>
                          <TableCell><Input type="number" value={item.rate} onChange={e => handleInvoiceItemChange(idx, 'rate', e.target.value)} className="w-24" /></TableCell>
                          <TableCell className="font-medium">Rs. {(item.quantity * item.rate).toLocaleString()}</TableCell>
                          <TableCell><Button type="button" variant="ghost" size="sm" onClick={() => removeInvoiceItem(idx)} disabled={invoiceForm.items.length === 1}><Trash2 className="h-4 w-4 text-red-500" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="text-right space-y-1 mt-2">
                    <div>Subtotal: Rs. {calculateInvoiceSubtotal().toLocaleString()}</div>
                    <div className="flex items-center justify-end gap-2">
                      <span>GST @</span>
                      <Input type="number" value={invoiceForm.gst_rate} onChange={e => setInvoiceForm({...invoiceForm, gst_rate: parseFloat(e.target.value) || 0})} className="w-16" />
                      <span>%: Rs. {calculateInvoiceGST().toLocaleString()}</span>
                    </div>
                    <div className="font-bold text-lg">Total: Rs. {calculateInvoiceTotal().toLocaleString()}</div>
                  </div>
                </div>
                <div><Label>Notes</Label><Textarea value={invoiceForm.notes} onChange={e => setInvoiceForm({...invoiceForm, notes: e.target.value})} rows={2} /></div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={submitting} className="bg-brillien-600 hover:bg-brillien-700">{submitting ? "Saving..." : editingInvoice ? "Update Invoice" : "Create Invoice"}</Button>
                  <Button type="button" variant="outline" onClick={resetInvoiceForm}>Cancel</Button>
                </div>
              </form>
            </Card>
          )}

          {projectInvoices.length === 0 && !showInvoiceForm ? (
            <div className="text-center py-12 text-slate-500"><Receipt className="h-12 w-12 mx-auto mb-3 text-slate-300" /><p>No invoices created</p></div>
          ) : !showInvoiceForm && (
            <Table>
              <TableHeader>
                <TableRow><TableHead>Invoice No.</TableHead><TableHead>Client</TableHead><TableHead>Date</TableHead><TableHead>Total</TableHead><TableHead>Received</TableHead><TableHead>Balance</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {projectInvoices.map(inv => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-mono text-sm">{inv.invoice_number}</TableCell>
                    <TableCell>{inv.client_name}</TableCell>
                    <TableCell>{formatDate(inv.invoice_date)}</TableCell>
                    <TableCell className="font-medium">Rs. {(inv.total_amount || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-green-600">Rs. {(inv.paid_amount || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-amber-600">Rs. {(inv.balance_amount || 0).toLocaleString()}</TableCell>
                    <TableCell><Badge className={statusColors[inv.status]}>{inv.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {canWrite && inv.balance_amount > 0 && <Button variant="ghost" size="sm" onClick={() => openPaymentForm('invoice', inv)} className="text-green-600" title="Record Payment"><DollarSign className="h-4 w-4" /></Button>}
                        {canWrite && <Button variant="ghost" size="sm" onClick={() => handleEditInvoice(inv)} className="text-blue-600"><Edit className="h-4 w-4" /></Button>}
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadInvoicePdf(inv.id)} className="text-brillien-600"><Download className="h-4 w-4" /></Button>
                        {canWrite && <Button variant="ghost" size="sm" onClick={() => handleDeleteInvoice(inv.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TabsContent>

        {/* EXPENSES TAB */}
        <TabsContent value="expenses">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold">Expenses</h4>
            {canWrite && !showExpenseForm && <Button onClick={() => setShowExpenseForm(true)} className="bg-brillien-600 hover:bg-brillien-700" data-testid="create-expense-btn"><Plus className="h-4 w-4 mr-2" />Add Expense</Button>}
          </div>

          {showExpenseForm && (
            <Card className="p-5 mb-4 border-brillien-200 bg-brillien-50/50">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-semibold">{editingExpense ? "Edit Expense" : "Add Expense"}</h4>
                <Button variant="ghost" size="sm" onClick={resetExpenseForm}><X className="h-4 w-4" /></Button>
              </div>
              <form onSubmit={handleSubmitExpense} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Description *</Label><Input value={expenseForm.description} onChange={e => setExpenseForm({...expenseForm, description: e.target.value})} required /></div>
                  <div><Label>Category</Label>
                    <Select value={expenseForm.category} onValueChange={v => setExpenseForm({...expenseForm, category: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Materials">Materials</SelectItem>
                        <SelectItem value="Labour">Labour</SelectItem>
                        <SelectItem value="Transport">Transport</SelectItem>
                        <SelectItem value="Equipment">Equipment</SelectItem>
                        <SelectItem value="Miscellaneous">Miscellaneous</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>Amount *</Label><Input type="number" value={expenseForm.amount} onChange={e => setExpenseForm({...expenseForm, amount: parseFloat(e.target.value) || 0})} required /></div>
                  <div><Label>Payment Mode</Label>
                    <Select value={expenseForm.payment_mode} onValueChange={v => setExpenseForm({...expenseForm, payment_mode: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Cash">Cash</SelectItem>
                        <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                        <SelectItem value="UPI">UPI</SelectItem>
                        <SelectItem value="Cheque">Cheque</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Date</Label><Input type="date" value={expenseForm.transaction_date} onChange={e => setExpenseForm({...expenseForm, transaction_date: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Status</Label>
                    <Select value={expenseForm.status} onValueChange={v => setExpenseForm({...expenseForm, status: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="Approved">Approved</SelectItem>
                        <SelectItem value="Paid">Paid</SelectItem>
                        <SelectItem value="Rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Remarks</Label><Input value={expenseForm.remarks} onChange={e => setExpenseForm({...expenseForm, remarks: e.target.value})} placeholder="Optional notes" /></div>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={submitting} className="bg-brillien-600 hover:bg-brillien-700">{submitting ? "Saving..." : editingExpense ? "Update" : "Add"}</Button>
                  <Button type="button" variant="outline" onClick={resetExpenseForm}>Cancel</Button>
                </div>
              </form>
            </Card>
          )}

          {projectExpenses.length === 0 && !showExpenseForm ? (
            <div className="text-center py-12 text-slate-500"><Receipt className="h-12 w-12 mx-auto mb-3 text-slate-300" /><p>No expenses recorded</p></div>
          ) : !showExpenseForm && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Status</TableHead>
                  {canWrite && <TableHead>Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {projectExpenses.map(exp => (
                  <TableRow key={exp.id}>
                    <TableCell>{formatDate(exp.transaction_date)}</TableCell>
                    <TableCell>{exp.description}</TableCell>
                    <TableCell>{exp.category || "-"}</TableCell>
                    <TableCell className="font-medium">Rs. {(exp.amount || 0).toLocaleString()}</TableCell>
                    <TableCell>{exp.payment_mode}</TableCell>
                    <TableCell>
                      <Select 
                        value={exp.status || "Pending"} 
                        onValueChange={(v) => handleUpdateExpenseStatus(exp, v)}
                        disabled={!canWrite}
                      >
                        <SelectTrigger className="w-28 h-8">
                          <Badge className={statusColors[exp.status || "Pending"]}>{exp.status || "Pending"}</Badge>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Pending">Pending</SelectItem>
                          <SelectItem value="Approved">Approved</SelectItem>
                          <SelectItem value="Paid">Paid</SelectItem>
                          <SelectItem value="Rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEditExpense(exp)} className="text-blue-600" data-testid={`edit-expense-${exp.id}`}>
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TabsContent>
      </Tabs>

      {/* Payment Recording Dialog */}
      <Dialog open={showPaymentForm} onOpenChange={() => setShowPaymentForm(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record Payment - {paymentTarget?.item?.id}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmitPayment} className="space-y-4">
            <div><Label>Amount *</Label><Input type="number" value={paymentForm.amount} onChange={e => setPaymentForm({...paymentForm, amount: parseFloat(e.target.value) || 0})} required /></div>
            <div><Label>Payment Date *</Label><Input type="date" value={paymentForm.payment_date} onChange={e => setPaymentForm({...paymentForm, payment_date: e.target.value})} required /></div>
            <div><Label>Payment Mode *</Label>
              <Select value={paymentForm.payment_mode} onValueChange={v => setPaymentForm({...paymentForm, payment_mode: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Reference No.</Label><Input value={paymentForm.reference_no} onChange={e => setPaymentForm({...paymentForm, reference_no: e.target.value})} /></div>
            <div><Label>Remarks</Label><Textarea value={paymentForm.remarks} onChange={e => setPaymentForm({...paymentForm, remarks: e.target.value})} rows={2} /></div>
            <div className="flex gap-2">
              <Button type="submit" disabled={submitting} className="bg-brillien-600 hover:bg-brillien-700">{submitting ? "Recording..." : "Record Payment"}</Button>
              <Button type="button" variant="outline" onClick={() => setShowPaymentForm(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
